import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const readToken = () => {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token") || "";
  if (token) window.history.replaceState(null, "", window.location.pathname);
  return token;
};

export default function ResetPassword() {
  const { setUser } = useAuth();
  const [token] = useState(readToken);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError("Use at least 8 characters");
    if (password !== confirm) return setError("Passwords don't match");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(false);
      setDone(data.message);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token && !done) return (
    <AuthLayout testId="reset-page" headline="Back to your circle.">
      <h1 className="font-display text-5xl leading-[0.95]">Reset link missing</h1>
      <p className="mt-4 text-muted-foreground" data-testid="reset-missing-token">Open the link from your reset email, or request a new one.</p>
      <Link to="/forgot-password" className="btn-primary mt-8" data-testid="reset-request-new">Request a new link</Link>
    </AuthLayout>
  );

  return (
    <AuthLayout testId="reset-page" headline="Back to your circle.">
      <h1 className="font-display text-5xl leading-[0.95] sm:text-6xl">Choose a new password</h1>
      {done ? (
        <div className="surface mt-10 p-6" role="status" data-testid="reset-success">
          <CheckCircle2 className="h-7 w-7 text-primary" aria-hidden />
          <p className="mt-4">{done} You've been signed out on every device.</p>
          <Link to="/signin" className="btn-primary mt-6" data-testid="reset-signin-link">Sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="mt-10 space-y-6">
          <div>
            <label htmlFor="rp-password" className="field-label">New password</label>
            <input id="rp-password" type="password" autoComplete="new-password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
            <p className="mt-2 text-xs text-muted-foreground">At least 8 characters.</p>
          </div>
          <div>
            <label htmlFor="rp-confirm" className="field-label">Confirm new password</label>
            <input id="rp-confirm" type="password" autoComplete="new-password" className="field" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
          </div>
          {error && <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm" data-testid="reset-error">{error}</p>}
          <button type="submit" className="btn-primary min-h-[56px] w-full text-lg" disabled={busy} data-testid="reset-submit-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
          </button>
          <p className="text-center text-sm text-muted-foreground"><Link to="/forgot-password" className="text-primary underline underline-offset-4" data-testid="reset-request-new">Request a new link</Link></p>
        </form>
      )}
    </AuthLayout>
  );
}
